import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import { useParams } from "react-router";
import {
  computeQuote,
  formatEuro,
  formatIban,
  ibanValid,
  normalizeIban,
  quoteTerms,
  quoteTitle,
  SALES_BASE_COPY,
  SALES_MODULE_COPY,
  type PublicQuoteDto,
  type SalesAcceptBody,
  type SalesAgreementReceipt,
  type SalesModuleCopy,
} from "@klantpunt/shared";
import { BrandMark } from "../app/BrandMark";
import { IconDownload } from "../app/icons";
import { Meta } from "../app/Meta";
import { ParentCompany } from "../app/ParentCompany";
import { QuoteSummary } from "../features/platform/QuoteSummary";
import { api, ApiError } from "../lib/api";

const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Er ging iets mis");
const dayLong = (iso: string | null) =>
  iso
    ? new Date(`${iso}T12:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" })
    : "";
const moment = (iso: string) => new Date(iso).toLocaleString("nl-NL", { dateStyle: "long", timeStyle: "short" });
/** "5.600" of "5600,50" → centen; leeg = geen budget. */
const parseEuro = (v: string): number | null | undefined => {
  const s = v.replace(/[€\s]/g, "");
  if (!s) return null;
  const n = Number(s.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : undefined;
};

/**
 * Openbare akkoordpagina /offerte/:token (docs/VERKOOP.md stap 3): de klant leest het voorstel en geeft
 * akkoord met PO-nummer (of kenmerk) en IBAN. Dat akkoord is de overeenkomst. Geen inloggen nodig.
 */
export function QuotePublicPage() {
  const { token = "" } = useParams();
  const q = useQuery({
    queryKey: ["offerte", token],
    queryFn: () => api<PublicQuoteDto>(`/public/offerte/${encodeURIComponent(token)}`),
    retry: false,
  });
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState("");

  const shell = (body: ReactNode, pdf?: () => void) => (
    <div className="qrpublic">
      <header className="qrhead">
        <BrandMark />
        {pdf && (
          <button type="button" className="btn small" onClick={pdf} disabled={pdfBusy}>
            <IconDownload size={18} aria-hidden="true" />
            {pdfBusy ? "PDF maken…" : "Offerte als PDF"}
          </button>
        )}
      </header>
      <main className="page offer-page">
        {body}
        <ParentCompany />
      </main>
    </div>
  );

  if (q.isPending)
    return shell(
      <p className="muted" role="status">
        Voorstel laden…
      </p>,
    );
  if (q.isError)
    return shell(
      <>
        <h1 className="dot">Voorstel</h1>
        <div className="alert" role="alert">
          {errMsg(q.error)}
        </div>
      </>,
    );

  const { quote: d, state, receipt, sellerIncomplete } = q.data;
  const input = d.input;
  const totals = computeQuote(input, d.prices);
  const seller = d.seller.tradeName || "Klantpunt";
  const copies: SalesModuleCopy[] = [SALES_BASE_COPY, ...totals.included.map((k) => SALES_MODULE_COPY[k])];

  const downloadPdf = async () => {
    setPdfBusy(true);
    setPdfError("");
    try {
      const { downloadQuotePdf } = await import("../features/platform/quotePdf");
      await downloadQuotePdf(d);
    } catch (e) {
      setPdfError(errMsg(e));
    } finally {
      setPdfBusy(false);
    }
  };

  return shell(
    <>
      <p className="label">
        Voorstel {d.number} voor {d.customer.name}
      </p>
      <h1 className="dot">{quoteTitle(input)}</h1>
      <p className="muted offer-intro">
        {input.intro ||
          `Hieronder staat wat ${seller} voor ${d.customer.name} voorstelt, wat het kost en hoe je akkoord geeft.`}
      </p>
      <Meta
        className="muted small"
        items={[
          `${formatEuro(totals.monthly)} per maand excl. btw`,
          `${input.buildings} ${input.buildings === 1 ? "gebouw" : "gebouwen"}`,
          `${input.termMonths} maanden`,
          input.startOn && `vanaf ${dayLong(input.startOn)}`,
          input.validUntil && `geldig tot ${dayLong(input.validUntil)}`,
        ]}
      />
      {(state === "open" || state === "accepted") && (
        <a className="btn small offer-jump" href="#offer-side">
          {state === "accepted" ? "Naar het akkoord" : "Naar akkoord geven"}
        </a>
      )}
      {pdfError && (
        <p className="alert" role="alert">
          {pdfError}
        </p>
      )}
      {state === "preview" && (
        <p className="alert info">
          Voorbeeld: zo ziet de klant deze pagina. Tekenen kan pas als de offerte is verstuurd.
        </p>
      )}

      <div className="co-grid offer-grid">
        <div className="co-col">
          <section className="panel co-panel" aria-labelledby="offer-what">
            <h2 id="offer-what">Wat je krijgt</h2>
            {copies.map((c, i) => (
              <details key={c.name} className="offer-mod" open={i < 2}>
                <summary>{c.name}</summary>
                <p className="muted">{c.line}</p>
                <ul className="offer-list">
                  {c.can.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </details>
            ))}
          </section>
          <QuoteSummary totals={totals} input={input} />
          <section className="panel co-panel" aria-labelledby="offer-terms">
            <h2 id="offer-terms">Voorwaarden in het kort</h2>
            <dl className="offer-terms">
              {quoteTerms(input, seller).map(([h, t]) => (
                <div key={h}>
                  <dt>{h}</dt>
                  <dd className="muted">{t}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>
        <div className="co-col" id="offer-side">
          {state === "accepted" && receipt ? (
            <Receipt r={receipt} seller={seller} />
          ) : state === "open" || state === "preview" ? (
            sellerIncomplete && state === "open" ? (
              <section className="panel co-panel">
                <h2>Akkoord geven</h2>
                <p className="muted">
                  Dit voorstel kan nog niet online worden getekend. Neem contact op met {seller}
                  {d.seller.email && (
                    <>
                      {" "}
                      via <a href={`mailto:${d.seller.email}`}>{d.seller.email}</a>
                    </>
                  )}
                  .
                </p>
              </section>
            ) : (
              <SignForm data={q.data} token={token} preview={state === "preview"} />
            )
          ) : (
            <section className="panel co-panel">
              <h2>{state === "expired" ? "Voorstel verlopen" : "Voorstel afgewezen"}</h2>
              <p className="muted">
                {state === "expired"
                  ? `Dit voorstel was geldig tot ${dayLong(d.validUntil)}. Wil je toch verder? Vraag een nieuw voorstel aan`
                  : "Dit voorstel is afgewezen of ingetrokken. Vragen? Neem contact op"}
                {d.seller.email ? (
                  <>
                    {" "}
                    via{" "}
                    <a href={`mailto:${d.seller.email}?subject=${encodeURIComponent(d.number)}`}>{d.seller.email}</a>
                  </>
                ) : (
                  ` met ${seller}`
                )}
                .
              </p>
            </section>
          )}
        </div>
      </div>
    </>,
    () => void downloadPdf(),
  );
}

function SignForm({ data, token, preview }: { data: PublicQuoteDto; token: string; preview: boolean }) {
  const d = data.quote;
  const input = d.input;
  const totals = computeQuote(input, d.prices);
  const contact = d.contacts[0];
  const seller = d.seller.legalName || d.seller.tradeName || "Klantpunt";
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: contact?.name ?? "",
    role: contact?.role ?? "",
    email: contact?.email ?? "",
    invoiceEmail: "",
    billingAddress: "",
    noPo: false,
    poNumber: "",
    reference: "",
    poBudget: "",
    poUntil: "",
    iban: "",
    ibanHolder: d.customer.name,
    agree: false,
  });
  const [touched, setTouched] = useState(false);
  const set = (p: Partial<typeof f>) => setF((v) => ({ ...v, ...p }));

  const ibanOk = ibanValid(f.iban);
  const ibanLen = normalizeIban(f.iban).length;
  const budget = parseEuro(f.poBudget);
  const errors = {
    name: f.name.trim().length < 2 ? "Vul je naam in." : "",
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()) ? "" : "Vul een geldig e-mailadres in.",
    invoiceEmail:
      !f.invoiceEmail.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.invoiceEmail.trim())
        ? ""
        : "Vul een geldig e-mailadres in.",
    poNumber: !f.noPo && f.poNumber.trim().length < 2 ? "Vul jullie inkoopnummer in." : "",
    poBudget: !f.noPo && budget === undefined ? "Vul een bedrag in, bijvoorbeeld 5.600." : "",
    iban: ibanOk ? "" : "Dit rekeningnummer klopt niet. Controleer de cijfers.",
    agree: f.agree ? "" : "Vink aan dat je akkoord gaat.",
  };
  const valid = Object.values(errors).every((e) => !e);

  const sign = useMutation({
    mutationFn: () => {
      const body: SalesAcceptBody = {
        version: d.version,
        name: f.name.trim(),
        role: f.role.trim(),
        email: f.email.trim(),
        invoiceEmail: f.invoiceEmail.trim(),
        billingAddress: f.billingAddress.trim(),
        noPo: f.noPo,
        poNumber: f.noPo ? "" : f.poNumber.trim(),
        reference: f.noPo ? f.reference.trim() : "",
        poBudget: f.noPo ? null : (budget ?? null),
        poUntil: f.noPo || !f.poUntil ? null : f.poUntil,
        iban: normalizeIban(f.iban),
        ibanHolder: f.ibanHolder.trim(),
        agree: true,
      };
      return api<SalesAgreementReceipt>(`/public/offerte/${encodeURIComponent(token)}/akkoord`, {
        method: "POST",
        body,
      });
    },
    onSuccess: (receipt) =>
      qc.setQueryData<PublicQuoteDto>(["offerte", token], (old) =>
        old ? { ...old, state: "accepted", receipt } : old,
      ),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || preview) return;
    sign.mutate();
  };
  const err = (k: keyof typeof errors, id: string) =>
    touched && errors[k] ? (
      <span className="hint err" id={id}>
        {errors[k]}
      </span>
    ) : null;
  const invalid = (k: keyof typeof errors) => (touched && errors[k] ? true : undefined);

  return (
    <section className="panel co-panel offer-sign" aria-labelledby="offer-sign">
      <h2 id="offer-sign">Akkoord geven</h2>
      <p className="muted small">
        Velden met * zijn nodig. Het {input.allowNoPo ? "PO-nummer of jullie kenmerk" : "PO-nummer"} komt op elke
        factuur.
      </p>
      <form onSubmit={submit} noValidate>
        <div className="grid2">
          <label className="field">
            <span>Naam *</span>
            <input
              value={f.name}
              onChange={(e) => set({ name: e.target.value })}
              autoComplete="name"
              aria-invalid={invalid("name")}
              aria-describedby="err-name"
            />
            {err("name", "err-name")}
          </label>
          <label className="field">
            <span>Functie</span>
            <input value={f.role} onChange={(e) => set({ role: e.target.value })} autoComplete="organization-title" />
          </label>
        </div>
        <label className="field">
          <span>E-mail *</span>
          <input
            type="email"
            value={f.email}
            onChange={(e) => set({ email: e.target.value })}
            autoComplete="email"
            aria-invalid={invalid("email")}
            aria-describedby="err-email"
          />
          {err("email", "err-email")}
        </label>

        {input.allowNoPo && (
          <label className="check offer-check">
            <input type="checkbox" checked={f.noPo} onChange={(e) => set({ noPo: e.target.checked })} />
            <span>Wij werken zonder PO-nummer</span>
          </label>
        )}
        {f.noPo ? (
          <label className="field">
            <span>Eigen kenmerk op de factuur</span>
            <input
              value={f.reference}
              onChange={(e) => set({ reference: e.target.value })}
              placeholder="bijv. kostenplaats of afdeling"
            />
            <span className="hint">Optioneel. Komt op elke factuur waar anders het PO-nummer staat.</span>
          </label>
        ) : (
          <>
            <label className="field">
              <span>PO-nummer *</span>
              <input
                value={f.poNumber}
                onChange={(e) => set({ poNumber: e.target.value })}
                placeholder="bijv. 4500-2291"
                aria-invalid={invalid("poNumber")}
                aria-describedby="err-po"
              />
              {err("poNumber", "err-po") ?? <span className="hint">Jullie inkoop- of bestelnummer.</span>}
            </label>
            <div className="grid2">
              <label className="field">
                <span>Budget op dit PO</span>
                <input
                  className="num"
                  inputMode="decimal"
                  value={f.poBudget}
                  onChange={(e) => set({ poBudget: e.target.value })}
                  placeholder="bijv. 5.600"
                  aria-invalid={invalid("poBudget")}
                  aria-describedby="err-budget"
                />
                {err("poBudget", "err-budget") ?? (
                  <span className="hint">Optioneel, excl. btw. Eerste jaar: {formatEuro(totals.firstYear)}.</span>
                )}
              </label>
              <label className="field">
                <span>PO geldig tot</span>
                <input type="date" value={f.poUntil} onChange={(e) => set({ poUntil: e.target.value })} />
                <span className="hint">Optioneel.</span>
              </label>
            </div>
          </>
        )}

        <label className="field">
          <span>Rekeningnummer (IBAN) *</span>
          <input
            className="num"
            value={f.iban}
            onChange={(e) => set({ iban: e.target.value })}
            onBlur={() => ibanOk && set({ iban: formatIban(f.iban) })}
            placeholder="NL00 BANK 0000 0000 00"
            autoComplete="off"
            spellCheck={false}
            aria-invalid={invalid("iban") ?? (ibanLen >= 18 && !ibanOk ? true : undefined)}
            aria-describedby="err-iban"
          />
          {ibanOk ? (
            <span className="hint ok" id="err-iban">
              Geldig rekeningnummer
            </span>
          ) : touched || ibanLen >= 18 ? (
            <span className="hint err" id="err-iban">
              {errors.iban}
            </span>
          ) : (
            <span className="hint" id="err-iban">
              Van jullie organisatie, voor de facturatie.
            </span>
          )}
        </label>
        <label className="field">
          <span>Tenaamstelling</span>
          <input value={f.ibanHolder} onChange={(e) => set({ ibanHolder: e.target.value })} />
        </label>
        <label className="field">
          <span>Facturen naar</span>
          <input
            type="email"
            value={f.invoiceEmail}
            onChange={(e) => set({ invoiceEmail: e.target.value })}
            placeholder={f.email || "facturen@…"}
            aria-invalid={invalid("invoiceEmail")}
            aria-describedby="err-inv"
          />
          {err("invoiceEmail", "err-inv") ?? <span className="hint">Leeg = naar het e-mailadres hierboven.</span>}
        </label>
        <label className="field">
          <span>Factuuradres</span>
          <textarea
            rows={3}
            value={f.billingAddress}
            onChange={(e) => set({ billingAddress: e.target.value })}
            placeholder={"Straat en huisnummer\nPostcode en plaats"}
            autoComplete="street-address"
          />
        </label>

        <label className="check offer-check">
          <input
            type="checkbox"
            checked={f.agree}
            onChange={(e) => set({ agree: e.target.checked })}
            aria-invalid={invalid("agree")}
            aria-describedby="err-agree"
          />
          <span>
            Ik ga akkoord met dit voorstel, de algemene voorwaarden en de verwerkersovereenkomst van {seller}, en ben
            bevoegd om namens {d.customer.name} te tekenen. *
          </span>
        </label>
        {err("agree", "err-agree")}

        {sign.isError && (
          <p className="alert" role="alert">
            {errMsg(sign.error)}
          </p>
        )}
        <button type="submit" className="btn primary offer-submit" disabled={sign.isPending || preview}>
          {sign.isPending ? "Akkoord vastleggen…" : `Akkoord geven · ${formatEuro(totals.monthly)} per maand`}
        </button>
        <p className="muted small offer-fine">
          We leggen je gegevens, het tijdstip en je IP-adres vast bij versie {d.version || 1} van dit voorstel.
        </p>
      </form>
    </section>
  );
}

function Receipt({ r, seller }: { r: SalesAgreementReceipt; seller: string }) {
  return (
    <section className="panel co-panel offer-done" aria-labelledby="offer-done" aria-live="polite">
      <h2 id="offer-done">Akkoord vastgelegd</h2>
      <p className="muted">
        Dank je, {r.signerName.split(/\s+/)[0]}. {seller} neemt contact op om jullie omgeving klaar te zetten.
      </p>
      <dl className="kv">
        <dt>Voorstel</dt>
        <dd className="num">
          {r.number} · versie {r.version}
        </dd>
        <dt>Akkoord door</dt>
        <dd>
          {r.signerName}
          {r.signerRole && <span className="muted"> · {r.signerRole}</span>}
        </dd>
        <dt>Tijdstip</dt>
        <dd>{moment(r.acceptedAt)}</dd>
        <dt>{r.noPo ? "Kenmerk" : "PO-nummer"}</dt>
        <dd className="num">{r.noPo ? r.reference || "Zonder PO" : r.poNumber}</dd>
        {r.poBudget !== null && (
          <>
            <dt>PO-budget</dt>
            <dd className="num">
              {formatEuro(r.poBudget)}
              {r.poUntil && <span className="muted"> tot {dayLong(r.poUntil)}</span>}
            </dd>
          </>
        )}
        <dt>Rekening</dt>
        <dd className="num">{r.ibanMasked}</dd>
        <dt>Per maand</dt>
        <dd className="num">{formatEuro(r.monthly)} excl. btw</dd>
        {r.startOn && (
          <>
            <dt>Ingangsdatum</dt>
            <dd>{dayLong(r.startOn)}</dd>
          </>
        )}
      </dl>
    </section>
  );
}
